import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

// Rule of thumb (decision tree):
// 1) Need near real-time freshness or polling? -> realtime
// 2) Changes periodically and should auto-refresh on focus? -> semiDynamic
// 3) Rarely changes and refreshed by mutation invalidation? -> static

type StrategyOptions = Pick<
  UseQueryOptions<unknown, Error, unknown, QueryKey>,
  "staleTime" | "gcTime" | "refetchOnWindowFocus" | "refetchInterval" | "retry"
>;

export const queryStrategies = {
  // Use for data that must stay fresh almost all the time.
  // Typical cases: live dashboard metrics, notifications, market/price feeds.
  // Tradeoff: more network traffic due to frequent refetch/polling.
  realtime: {
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 1000,
    retry: 1,
  },
  // Use for business data that changes sometimes and should refresh naturally.
  // Typical cases: user profile, order lists, task lists, management pages.
  // Keeps data fresh enough without the cost of continuous polling.
  semiDynamic: {
    staleTime: 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  },
  // Use for lookup/reference data that rarely changes.
  // Typical cases: catalog filters, enums, countries, static settings.
  // Refresh this data manually via invalidateQueries after mutations.
  static: {
    staleTime: Infinity,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  },
} satisfies Record<string, StrategyOptions>;

export type QueryStrategyName = keyof typeof queryStrategies;
